// EnhancedLoadingScreen.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper } from '@mantine/core';
import { useSelector } from 'react-redux';
import { TypeAnimation } from 'react-type-animation';
import { motion, AnimatePresence } from 'motion/react';
import AIGeneratingIcon from './AIGeneratingIcon';
import Logo from '../images/Logo_Black_Smooth.png';

// Prevent duplicate "first-render" animations under React 18 StrictMode remounts
let hasShownInitialAnimation = false;

const greetings = [
  { text: "Hi, I'm Huginn", emoji: "👋" },
  { text: "I'm here to help you build safer AI", emoji: "🛡️" },
  { text: "Let's get to work", emoji: "🤝" },
];

const SESSION_FLAG = 'huginn_session_active';

export const MinimalSpinner = () => (
  <Box style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    width: '100vw',
    backgroundColor: '#ffffff',
  }}>
    <Box style={{ position: 'relative', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div
        style={{
          position: 'absolute', top: 0, left: 0, width: 56, height: 56,
          borderRadius: '50%', background: '#f0f4f8',
          boxShadow: '0 0 8px 0 rgba(96,125,203,0.2)',
        }}
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 2, repeat: Infinity, easing: 'ease-in-out' }}
      />
      <Box style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AIGeneratingIcon isGenerating size={32} color="#6574cd" />
      </Box>
    </Box>
  </Box>
);

const EnhancedLoadingScreen = ({ message, forceShow = false, onComplete }) => {

  // Use RTK state for loading check (models.loading or app.isInitializing)
  const modelsLoading = useSelector((s) => s?.models?.loading || false);
  const appInitializing = useSelector((s) => s?.app?.isInitializing || false);
  const isLoading = modelsLoading || appInitializing;

  // Detect if this is a returning session (refresh/navigation) vs fresh login
  const isReturningSession = useRef(sessionStorage.getItem(SESSION_FLAG) === '1');

  // Track if minimum animation time has passed (greetings need ~4 seconds to complete)
  const [minTimeElapsed, setMinTimeElapsed] = useState(isReturningSession.current);

  // Show loading screen until BOTH data is loaded AND animation completes
  const show = forceShow || isLoading || !minTimeElapsed;

  const [animationsStarted, setAnimationsStarted] = useState(false);
  // phases: 'logo' → 'greetings' → 'done'
  const [phase, setPhase] = useState('logo');
  // Prevent re-running greetings if something re-renders mid-flow
  const [hasFinishedGreetings, setHasFinishedGreetings] = useState(false);
  // Brief "Thinking…" inside the bubble before typing starts
  const [showThinking, setShowThinking] = useState(false);

  const mountedRef = useRef(true);
  const isFirstVisualMountRef = useRef(!hasShownInitialAnimation);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    // Skip animation timers for returning sessions
    if (isReturningSession.current) return;

    mountedRef.current = true;

    const animTimer = setTimeout(() => {
      if (mountedRef.current) setAnimationsStarted(true);
    }, 100);

    const logoTimer = setTimeout(() => {
      if (mountedRef.current) setPhase('greetings');
    }, 1200);

    return () => {
      mountedRef.current = false;
      clearTimeout(animTimer);
      clearTimeout(logoTimer);
    };
  }, []);

  // Mark that the initial animation has been shown (survives StrictMode remounts)
  useEffect(() => {
    if (!hasShownInitialAnimation) {
      hasShownInitialAnimation = true;
    }
  }, []);


  useEffect(() => {
    if (phase === 'greetings' && !isReturningSession.current) {
      setShowThinking(true);
      const id = setTimeout(() => setShowThinking(false), 900);
      return () => clearTimeout(id);
    }
  }, [phase]);

  if (!show) return null;

  // Returning session: show minimal pulsating robot spinner
  if (isReturningSession.current) {
    return <MinimalSpinner />;
  }

  return (
    <Box style={{
      opacity: show ? 1 : 0,
      visibility: show ? 'visible' : 'hidden',
      pointerEvents: show ? 'auto' : 'none',
      transition: 'opacity 160ms ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      width: '100vw',
      backgroundColor: '#ffffff',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated logo/greetings sequence */}
      <AnimatePresence mode="wait" initial={false}>
        {/* Show logo only during initial phase */}
        {phase === 'logo' && (
          <motion.img
            key={`logo-${phase}`}
            src={Logo}
            alt="Huginn logo"
            initial={isFirstVisualMountRef.current ? { opacity: 0, y: 8 } : false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.5 }}
            style={{
              maxWidth: 'clamp(160px, 20vw, 220px)',
              maxHeight: '80px',
              objectFit: 'contain',
              marginBottom: '1.25rem'
            }}
          />
        )}

        {/* Render greetings only once, with "Thinking…" then typewriter in bubble */}
        {phase === 'greetings' && !hasFinishedGreetings && (
          <motion.div
            key="greetings"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.4 }}
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            {/* Grid with 2 columns: icon on left, greetings on right */}
            <Box style={{
              display: 'grid',
              gridTemplateColumns: '48px 1fr',
              gap: '8px',
              alignItems: 'center',
              minHeight: '48px',
              width: 'min(100%, 720px)',
              maxWidth: '100%',
              margin: '0 auto 0.5rem auto'
            }}>
              {/* Left grid cell: Static pulsating icon */}
              <Box
                style={{
                  position: 'relative',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {/* The pulsating bright circle */}
                <motion.div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    boxSizing: 'border-box',
                    zIndex: 1,
                    background: '#f0f4f8',
                    boxShadow: '0 0 8px 0 rgba(96,125,203,0.2)',
                  }}
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 2, repeat: Infinity, easing: 'ease-in-out' }}
                />
                {/* The icon, centered */}
                <Box
                  style={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <AIGeneratingIcon isGenerating size={28} color="#6574cd" />
                </Box>
              </Box>

              {/* Right grid cell: "Thinking…" or typewriter text inside chat bubble */}
              <Box style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                minHeight: '40px',
                minWidth: 0,
                overflow: 'hidden'
              }}>
                <Paper withBorder radius="lg" p="md" shadow="xs" style={{
                  width: '100%',
                  background: '#343541',
                  borderColor: '#2f3037'
                }}>
                  <AnimatePresence mode="wait">
                    {showThinking && (
                      <motion.div
                        key="thinking"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{ color: '#d1d5db', fontSize: '0.95rem' }}
                      >
                        Thinking…
                      </motion.div>
                    )}

                    {!showThinking && animationsStarted && (
                      <TypeAnimation
                        sequence={[
                          `${greetings[0].text} ${greetings[0].emoji}`, 800,
                          `${greetings[1].text} ${greetings[1].emoji}`, 800,
                          `${greetings[2].text} ${greetings[2].emoji}`, 800,
                          () => {
                            setHasFinishedGreetings(true);
                            setPhase('done');
                            setMinTimeElapsed(true);
                            // Mark session as active so refreshes skip the greeting
                            sessionStorage.setItem(SESSION_FLAG, '1');
                            if (onCompleteRef.current) onCompleteRef.current();
                          },
                        ]}
                        wrapper="div"
                        speed={60}
                        omitDeletionAnimation
                        cursor={true}
                        repeat={0}
                        style={{
                          display: 'block',
                          fontSize: '1rem',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, "Apple Color Emoji", "Segoe UI Emoji"'
                        }}
                      />
                    )}
                  </AnimatePresence>
                </Paper>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

    </Box>
  );
};

export default EnhancedLoadingScreen;
// // EnhancedLoadingScreen.jsx
// import React, { useState, useEffect, useRef } from 'react';
// import { Box, Text } from '@mantine/core';
// import { useSelector } from 'react-redux';
// import { TypeAnimation } from 'react-type-animation';
// import { motion } from 'motion/react';
// import AIGeneratingIcon from './AIGeneratingIcon';
// import Logo from '../images/logobw.svg';

// const greetings = [
//   { text: "Hi, I'm RiskGPT", emoji: "👋" },
//   { text: "I'm here to help you make AI a safer place", emoji: "🛡️" },
//   { text: "Let's get to work", emoji: "🤝" },
// ];

// const getLoadingStatus = (msg) => {
//   const lower = (msg || '').toLowerCase();
//   if (lower.includes('model') || lower.includes('inventory')) return 'loading models';
//   if (lower.includes('risk') && !lower.includes('security')) return 'analyzing risks';
//   if (lower.includes('security') || lower.includes('threat')) return 'identifying threats';
//   if (lower.includes('controls') || lower.includes('evaluation')) return 'fetching controls';
//   if (lower.includes('compliance') || lower.includes('regulation')) return 'checking compliance';
//   if (lower.includes('data') || lower.includes('loading')) return 'fetching from DB';
//   return 'initializing';
// };

// const EnhancedLoadingScreen = ({ message, forceShow = false }) => {
//   const loadingMessage = useSelector((s) => s?.risks?.loadingMessage || null);
//   const isLoading = useSelector((s) => s?.risks?.modelsLoading || false);
//   const show = forceShow || isLoading;

//   const [animationsStarted, setAnimationsStarted] = useState(false);
//   const [currentStatus, setCurrentStatus] = useState('');
//   const [showStatus, setShowStatus] = useState(false);

//   const statusIntervalRef = useRef(null);
//   const mountedRef = useRef(true);

//   useEffect(() => {
//     mountedRef.current = true;

//     const animTimer = setTimeout(() => {
//       if (mountedRef.current) setAnimationsStarted(true);
//     }, 100);

//     const statusTimer = setTimeout(() => {
//       if (mountedRef.current) setShowStatus(true);
//     }, 1500);

//     return () => {
//       mountedRef.current = false;
//       clearTimeout(animTimer);
//       clearTimeout(statusTimer);
//     };
//   }, []);

//   useEffect(() => {
//     if (!showStatus || !mountedRef.current) return;

//     const updateStatus = () => {
//       if (mountedRef.current) {
//         setCurrentStatus(getLoadingStatus(loadingMessage));
//       }
//     };

//     updateStatus();
//     statusIntervalRef.current = setInterval(updateStatus, 2000);

//     return () => {
//       clearInterval(statusIntervalRef.current);
//     };
//   }, [showStatus, loadingMessage]);

//   if (!show) return null;

//   return (
//     <Box style={{
//       opacity: show ? 1 : 0,
//       visibility: show ? 'visible' : 'hidden',
//       pointerEvents: show ? 'auto' : 'none',
//       transition: 'opacity 160ms ease',
//       display: 'flex',
//       flexDirection: 'column',
//       alignItems: 'center',
//       justifyContent: 'center',
//       height: '100vh',
//       width: '100vw',
//       backgroundColor: '#ffffff',
//       position: 'relative',
//       overflow: 'hidden',
//     }}>
//       {/* Logo - bigger and positioned first */}
//       <img 
//         src={Logo} 
//         alt="RiskGPT logo" 
//         style={{ 
//           width: 'clamp(120px, 16vw, 140px)', 
//           height: 'auto', 
//           maxHeight: '60px',
//           marginBottom: '1.5rem'
//         }} 
//       />

//       {/* Grid with 2 columns: icon on left, greetings on right */}
//       <Box style={{
//         display: 'grid',
//         gridTemplateColumns: '60px 1fr', // Fixed width for icon column, flexible for text
//         gap: '16px',
//         alignItems: 'center',
//         justifyContent: 'center',
//         marginBottom: '1rem',
//         minHeight: '40px',
//         maxWidth: '600px',
//         margin: '0 auto 1rem auto'
//       }}>
//         {/* Left grid cell: Static pulsating icon */}
//         <Box
//           style={{
//             position: 'relative',
//             width: '40px',
//             height: '40px',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             margin: '0 auto'
//           }}
//         >
//           {/* The pulsating bright circle */}
//           <motion.div
//             style={{
//               position: 'absolute',
//               top: 0,
//               left: 0,
//               width: '40px',
//               height: '40px',
//               borderRadius: '50%',
//               boxSizing: 'border-box',
//               zIndex: 1,
//               background: '#f0f4f8',
//               boxShadow: '0 0 8px 0 rgba(96,125,203,0.2)',
//             }}
//             animate={{ scale: [1, 1.08, 1] }}
//             transition={{ duration: 2, repeat: Infinity, easing: 'ease-in-out' }}
//           />
//           {/* The icon, centered */}
//           <Box
//             style={{
//               position: 'relative',
//               zIndex: 2,
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               width: '100%',
//               height: '100%',
//             }}
//           >
//             <AIGeneratingIcon isGenerating size={24} color="#6574cd" />
//           </Box>
//         </Box>

//         {/* Right grid cell: Typewriter text */}
//         <Box style={{
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'flex-start',
//           minHeight: '40px'
//         }}>
//           {animationsStarted && (
//             <TypeAnimation
//               sequence={[
//                 `${greetings[0].text} ${greetings[0].emoji}`, 800,
//                 `${greetings[1].text} ${greetings[1].emoji}`, 800,
//                 `${greetings[2].text} ${greetings[2].emoji}`, 800,
//               ]}
//               wrapper="span"
//               speed={60}
//               deletionSpeed={70}
//               cursor={true}
//               repeat={0}
//               style={{
//                 display: 'inline-flex',
//                 alignItems: 'center',
//                 fontSize: '1.5rem',
//                 color: '#6574cd',
//                 fontWeight: 400,
//                 lineHeight: '1.4',
//                 fontFamily: 'sans-serif',
//               }}
//             />
//           )}
//         </Box>
//       </Box>

//       {/* Status text - 24px below greetings */}
//       {showStatus && (
//         <Box
//           style={{ 
//             marginTop: '24px',
//             textAlign: 'center'
//           }}
//         >
//           <Text size="xs" fw={300} ta="center" style={{
//             color: '#888888',
//             fontSize: '0.75rem',
//             letterSpacing: '0.1px',
//             transition: 'opacity 0.4s ease',
//           }}>
//             {currentStatus}
//           </Text>
//         </Box>
//       )}
//     </Box>
//   );
// };

// export default EnhancedLoadingScreen;
